import { PropsWithChildren } from 'react';
import styled from 'styled-components';
import { rgba } from 'polished';
import * as Reach from '@reach/disclosure';

import noop from '#/utils/noop';

import ChevronDownIcon from '#/assets/ChevronDownIcon';

import { cFFFFFF } from '#/colors';

interface Props {
  readonly title: string;
  readonly collapsible?: boolean;
  /**
   * If not provided and `collapsible` is `true`,
   * it will be expanded by default (`true`).
   */
  readonly defaultExpanded?: boolean;
  /**
   * Controls whether the component is expanded or not.
   * When provided overrides `defaultExpanded`.
   * Only has effect when `collapsible` is `true`.
   */
  readonly expanded?: boolean;
  readonly onToggle?: () => void;
  readonly ContainerComponent?: React.ComponentType;
}

export default function DescriptionList(props: PropsWithChildren<Props>) {
  const {
    children,
    title,
    collapsible = false,
    expanded,
    defaultExpanded = expanded ?? true,
    onToggle,
    ContainerComponent = Container,
  } = props;

  return (
    <Reach.Disclosure
      open={expanded}
      defaultOpen={defaultExpanded}
      onChange={collapsible ? onToggle : noop}
    >
      <RootContainer>
        <Heading collapsible={collapsible}>
          <Reach.DisclosureButton type="button">
            {title}
            {collapsible && <ChevronDownIcon strokeWidth={1.4} />}
          </Reach.DisclosureButton>
        </Heading>
        <Reach.DisclosurePanel>
          <ContainerComponent>{children}</ContainerComponent>
        </Reach.DisclosurePanel>
      </RootContainer>
    </Reach.Disclosure>
  );
}

export const RootContainer = styled.div`
  display: grid;
  gap: 12px;
`;

export const Container = styled.div`
  display: grid;
  gap: 12px;
`;

export const GridAccordionItem = styled.div`
  display: grid;
  gap: 12px;
`;

export const Heading = styled.h3<{ readonly collapsible: boolean }>`
  font-size: 12px;
  margin: 0;

  [data-reach-disclosure-button] {
    border: none;
    background: none;
    color: inherit;
    cursor: ${({ collapsible }) => (collapsible ? 'pointer' : 'unset')};
    font-weight: 500;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    padding: 0;

    svg {
      color: ${cFFFFFF};
    }

    &[data-state='open'] {
      svg {
        color: ${rgba(cFFFFFF, 0.7)};
        rotate: 180deg;
      }
      &:hover svg {
        color: ${cFFFFFF};
      }
    }
  }
`;
